import React, { useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  NotificationsNoneOutlined,
  CheckCircle,
  AccessTime,
  AttachMoney,
  LocalOffer,
  RequirementIcon,
  LocalShippingIcon,
  RequestQuoteIcon,
  Inventory2,
  Inventory2Icon,
} from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import DataTable from "../Requirement/components/common/ReusableTable";

const FPDashboard = () => {
  const pieData = [
    { name: "Open", value: 33, color: "#007bffab" },
    { name: "Matched", value: 19, color: "#00c49f" },
    { name: "Negotiation", value: 14, color: "#ffbb28" },
    { name: "Ongoing", value: 10, color: "#ff8042" },
    { name: "Closed", value: 24, color: "#c300ffac" },
  ];

  const allStockData = [
    { name: "Maize - USA", stock: 2600, country: "USA" },
    {
      name: "Basmati Rice 1609 Steam - Pakistan",
      stock: 650,
      country: "Pakistan",
    },
    { name: "Maize - Argentina", stock: 1000, country: "Argentina" },
    { name: "Corn - Ukraine", stock: 800, country: "Ukraine" },
    { name: "Corn - Brazil", stock: 1950, country: "Brazil" },
    { name: "Basmati Rice 1609 Steam - India", stock: 1300, country: "India" },
  ];

  const allOfferData = [
    { country: "USA", offers: 21 },
    { country: "Germany", offers: 14 },
    { country: "China", offers: 7 },
    { country: "Brazil", offers: 14 },
    { country: "Japan", offers: 10 },
    { country: "UK", offers: 8 },
  ];

  const allRequirementData = [
    { country: "USA", requirements: 32 },
    { country: "Germany", requirements: 16 },
    { country: "China", requirements: 24 },
    { country: "Brazil", requirements: 20 },
    { country: "India", requirements: 8 },
    { country: "Japan", requirements: 12 },
    { country: "UK", requirements: 8 },
  ];

  const allRfqData = [
    { country: "USA", rfqs: 28 },
    { country: "Germany", rfqs: 14 },
    { country: "China", rfqs: 17 },
    { country: "Brazil", rfqs: 7 },
    { country: "India", rfqs: 21 },
    { country: "Japan", rfqs: 7 },
    { country: "UK", rfqs: 10 },
  ];

  const cards = [
    {
      title: "Active RFQs",
      value: 12,
      icon: <Inventory2 />,
      iconColor: "#1565c0",
      bgColor: "#e3f2fd",
      textColor: "#0d47a1",
    },
    {
      title: "Matched Offers",
      value: 8,
      icon: <CheckCircle />,
      iconColor: "#2e7d32",
      bgColor: "#e8f5e9",
      textColor: "#1b5e20",
    },
    {
      title: "Negotiations",
      value: 3,
      icon: <AccessTime />,
      iconColor: "#f9a825",
      bgColor: "#fffde7",
      textColor: "#f57f17",
    },
    {
      title: "Accepted Deals",
      value: 5,
      icon: <AttachMoney />,
      iconColor: "#6a1b9a",
      bgColor: "#f3e5f5",
      textColor: "#4a148c",
    },
  ];

  const tableData = [
    {
      type: "RFQ Raised",
      description: "New RFQ for Steel Bars",
      date: "2025-07-15",
      status: "Open",
      product: "Steel Bars",
      country: "USA",
    },
    {
      type: "Offer Matched",
      description: "Offer matched for Copper Wire",
      date: "2025-07-14",
      status: "Matched",
      product: "Copper Wire",
      country: "Germany",
    },
    {
      type: "Negotiation Started",
      description: "Negotiation for Aluminum Ingots",
      date: "2025-07-13",
      status: "Negotiation",
      product: "Aluminum Ingots",
      country: "China",
    },
    {
      type: "Deal Finalized",
      description: "Deal finalized for Iron Ore",
      date: "2025-07-12",
      status: "Closed",
      product: "Iron Ore",
      country: "Brazil",
    },
    {
      type: "RFQ Raised",
      description: "New RFQ for Plastic Pellets",
      date: "2025-07-11",
      status: "Open",
      product: "Plastic Pellets",
      country: "India",
    },
    {
      type: "Offer Matched",
      description: "Offer matched for Zinc Sheets",
      date: "2025-07-10",
      status: "Matched",
      product: "Zinc Sheets",
      country: "Japan",
    },
    {
      type: "Negotiation Started",
      description: "Negotiation for Rubber Tyres",
      date: "2025-07-09",
      status: "Negotiation",
      product: "Rubber Tyres",
      country: "UK",
    },
    {
      type: "Deal Finalized",
      description: "Deal finalized for Crude Oil",
      date: "2025-07-08",
      status: "Closed",
      product: "Crude Oil",
      country: "Saudi Arabia",
    },
    {
      type: "RFQ Raised",
      description: "New RFQ for Textiles",
      date: "2025-07-07",
      status: "Open",
      product: "Textiles",
      country: "Bangladesh",
    },
    {
      type: "Offer Matched",
      description: "Offer matched for Wheat",
      date: "2025-07-06",
      status: "Matched",
      product: "Wheat",
      country: "Canada",
    },
  ];

  const columns = [
    { id: "type", numeric: true, label: "TYPE" },
    { id: "description", numeric: false, label: "DESCRIPTION" },
    { id: "date", numeric: false, label: "DATE" },
    {
      id: "status",
      numeric: false,
      label: "STATUS",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.status) {
          case "Open":
            bgColor = "#e0f2fe";
            textColor = "#0369a1";
            break;
          case "Matched":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          case "Negotiation":
            bgColor = "#fef3c7";
            textColor = "#b45309";
            break;
          case "Closed":
            bgColor = "#e0e0e0";
            textColor = "#424242";
            break;
          default:
            bgColor = "#f3f4f6";
            textColor = "#374151";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.status}
          </span>
        );
      },
    },
    { id: "product", numeric: false, label: "PRODUCT" },
    { id: "country", numeric: false, label: "COUNTRY" },
  ];

  const countries = [
    "All",
    "USA",
    "Germany",
    "China",
    "Brazil",
    "India",
    "Japan",
    "UK",
  ];

  // State for each dropdown
  const [selectedStockCountry, setSelectedStockCountry] = useState("All");
  const [selectedOfferCountry, setSelectedOfferCountry] = useState("All");
  const [selectedRequirementCountry, setSelectedRequirementCountry] =
    useState("All");
  const [selectedRfqCountry, setSelectedRfqCountry] = useState("All");

  // Filtered data based on respective country selections
  const stockData =
    selectedStockCountry === "All"
      ? allStockData
      : allStockData.filter((item) => item.country === selectedStockCountry);

  const offerData =
    selectedOfferCountry === "All"
      ? allOfferData
      : allOfferData.filter((item) => item.country === selectedOfferCountry);

  const requirementData =
    selectedRequirementCountry === "All"
      ? allRequirementData
      : allRequirementData.filter(
          (item) => item.country === selectedRequirementCountry
        );

  const rfqData =
    selectedRfqCountry === "All"
      ? allRfqData
      : allRfqData.filter((item) => item.country === selectedRfqCountry);
  return (
    <div className="buyer-dashboard">
      <div className="card">
        <div className="row">
          {/* Top Cards */}
          <div className="row g-4 mb-4">
            {cards.map((card, index) => (
              <div className="col-12 col-sm-6 col-md-3" key={index}>
                <div
                  className="d-flex align-items-center shadow-sm rounded p-3 h-100"
                  style={{ backgroundColor: card.bgColor }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle me-3"
                    style={{
                      backgroundColor: "#ffffff",
                      color: card.iconColor,
                      width: "40px",
                      height: "40px",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    {card.icon}
                  </div>
                  <div>
                    <div
                      className="text-muted"
                      style={{ fontWeight: "bold", fontSize: "16px" }}
                    >
                      {card.title}
                    </div>
                    <div className="fw-bold" style={{ color: card.textColor }}>
                      {card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="row">
          {/* Pie Chart */}
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                Offers by Status
              </h6>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Stock Report Country Wise Bar Chart */}
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                Stock Report Country Wise
              </h6>
              <div style={{ marginBottom: "10px" }}>
                <label>Filter by Country:</label>
                <select
                  value={selectedStockCountry}
                  onChange={(e) => setSelectedStockCountry(e.target.value)}
                  className="form-control"
                  style={{ marginLeft: "10px" }}
                >
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={stockData}
                  margin={{ top: 10, right: 0, left: 0, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-15}
                    textAnchor="end"
                    height={5}
                    style={{ fontSize: "9px" }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="stock"
                    fill="#bccf75"
                    radius={[5, 5, 0, 0]}
                    name="Stock (MT)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* New Row with Offer and Requirement Reports */}
        <div className="row mt-4">
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />
                Offer Report Country Wise
              </h6>
              <div style={{ marginBottom: "10px" }}>
                <label>Filter by Country:</label>
                <select
                  value={selectedOfferCountry}
                  onChange={(e) => setSelectedOfferCountry(e.target.value)}
                  className="form-control"
                  style={{ marginLeft: "10px" }}
                >
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={offerData}
                  margin={{ top: 10, right: 0, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="country" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="offers" fill="#d7590d" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="col-lg-6 col-md-12">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                Requirement Report Country Wise
              </h6>
              <div style={{ marginBottom: "10px" }}>
                <label>Filter by Country:</label>
                <select
                  value={selectedRequirementCountry}
                  onChange={(e) =>
                    setSelectedRequirementCountry(e.target.value)
                  }
                  className="form-control"
                  style={{ marginLeft: "10px" }}
                >
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={requirementData}
                  margin={{ top: 10, right: 0, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="country" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="requirements"
                    fill="#ffbe00"
                    radius={[5, 5, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* RFQ Report Country Wise - Added after Requirement Report */}
        <div className="row mt-4">
          <div className="col-12 col-lg-6">
            <div
              className="bg-white p-4 rounded shadow-sm card"
              style={{ border: "1px solid #ddd" }}
            >
              <h6 className="fw-bold mb-2" style={{ fontSize: "16px" }}>
                <LocalOffer
                  style={{
                    verticalAlign: "middle",
                    marginRight: "8px",
                    color: "#1976d2",
                  }}
                />{" "}
                RFQ Report Country Wise
              </h6>
              <div style={{ marginBottom: "10px" }}>
                <label>Filter by Country:</label>
                <select
                  value={selectedRfqCountry}
                  onChange={(e) => setSelectedRfqCountry(e.target.value)}
                  className="form-control"
                  style={{ marginLeft: "10px" }}
                >
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={rfqData}
                  margin={{ top: 10, right: 0, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="country" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="rfqs" fill="#61822a" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="row mt-4">
          <div className="col-12">
            <Paper elevation={2} sx={{ p: 3 }}>
              <h6 className="fw-bold mb-4" style={{ fontSize: "16px" }}>
                Recent Activity
              </h6>
              <div className="table-content">
                <DataTable
                  columns={columns}
                  data={tableData}
                  // onRowClick={handleRowClick}
                />
              </div>
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FPDashboard;
